/** /kit — every base component in every state, for review and screenshots. Dev-facing. */
import { useState } from "react";

import { Button, DistributionBars, DraftChip, DraftFrame, OptionCard, StatusBar, Timer } from "../ui";
import type { ConnState, OptionState } from "../ui";

const bars = [
  { key: "A", label: "A", count: 16, correct: true, before: 9 },
  { key: "B", label: "B", count: 4, before: 7 },
  { key: "C", label: "C", count: 6, before: 10 },
  { key: "D", label: "D", count: 2, before: 2 },
];

export function Kit() {
  const [deadline] = useState(() => new Date(Date.now() + 90_000).toISOString());
  const [picked, setPicked] = useState<string | null>(null);
  const states: OptionState[] = ["default", "selected", "submitted", "correct", "incorrect", "disabled"];
  const conn: ConnState[] = ["live", "reconnecting", "offline", "syncing", "synced"];
  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-10 p-6">
      <section>
        <h2 className="mb-3 font-semibold">Button</h2>
        <div className="flex flex-wrap gap-3">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="tertiary">Tertiary</Button>
          <Button variant="destructive">Destructive</Button>
          <Button disabled>Disabled</Button>
          <Button loading>Loading</Button>
        </div>
      </section>
      <section>
        <h2 className="mb-3 font-semibold">Status bar</h2>
        <div className="flex flex-col gap-2">
          {conn.map((s) => (
            <StatusBar key={s} state={s} detail={s === "syncing" ? "3 sessions" : undefined} />
          ))}
        </div>
      </section>
      <section>
        <h2 className="mb-3 font-semibold">Draft chip and frame</h2>
        <div className="mb-4 flex gap-2">
          <DraftChip /> <DraftChip kind="cached" /> <DraftChip kind="edited" />
        </div>
        <DraftFrame kind="cached">
          <p>21% chose C: confusing slope with elasticity. Try: “Elasticity is a ratio of percentage changes, not the angle of the line.”</p>
        </DraftFrame>
      </section>
      <section>
        <h2 className="mb-3 font-semibold">Distribution bars</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div><p className="mb-1 text-sm">live</p><DistributionBars bars={bars} total={28} mode="live" /></div>
          <div><p className="mb-1 text-sm">revealed</p><DistributionBars bars={bars} total={28} mode="revealed" /></div>
          <div><p className="mb-1 text-sm">paired (before/after)</p><DistributionBars bars={bars} total={28} mode="paired" /></div>
          <div className="rounded-[var(--radius-card)] bg-[color:var(--projector-bg)] p-4 text-[color:var(--projector-ink)]"><p className="mb-1 text-sm">projector</p><DistributionBars bars={bars} total={28} mode="revealed" size="projector" counts={false} /></div>
        </div>
      </section>
      <section>
        <h2 className="mb-3 font-semibold">Timer</h2>
        <div className="flex items-center gap-8">
          <Timer deadline={deadline} />
          <Timer deadline={deadline} size="projector" />
        </div>
      </section>
      <section>
        <h2 className="mb-3 font-semibold">Option card</h2>
        <div role="radiogroup" aria-label="states" className="grid gap-3 md:grid-cols-2">
          {states.map((s) => (
            <OptionCard key={s} label={s[0]!.toUpperCase()} state={s} onSelect={() => setPicked(s)}>
              {s}
            </OptionCard>
          ))}
        </div>
        <p className="mt-2 text-sm text-[color:var(--ink-2)]">picked: {picked ?? "—"}</p>
      </section>
    </main>
  );
}
